import type { WorkflowReviewStatusData } from '@app/shared/types/sf-data'
import type {
  PreparedWorkflowOverviewAccountEntry,
  PreparedWorkflowOverviewReconciliationText,
  PulseReconciliationStatusData,
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
} from '@app/application/workflow-overview-navigation/types'

import fuzzysort from 'fuzzysort'

/**
 * Returns the single parents in a given hierarchy on the first level,
 * @param items - An array of items that can contain children
 * @returns - The parent id that is a single parent on the top or -1 if none exist
 */
export const exposeTopSingleParent = (
  items: Array<WorkflowOverviewItem | WorkflowOverviewGroup>
): number => {
  if (!items || items.length === 0) {
    return -1
  }

  const topLeveLParents = items.filter(
    (item) => item?.type === 'group' && item?.children?.length > 0
  )

  if (topLeveLParents.length === 1) {
    return topLeveLParents[0].id
  }

  return -1
}

export const groupReviewStatus = (
  group: WorkflowOverviewGroup,
  reviewStatus: Record<string, WorkflowReviewStatusData>,
  groupReviewStatus: Record<
    string,
    Pick<WorkflowReviewStatusData, 'review' | 'reviews'>
  >
): Pick<WorkflowReviewStatusData, 'review' | 'reviews'> => {
  const result = group.children.reduce(
    (acc, c) => {
      if (c.type === 'group') {
        acc.groupReviewed =
          acc.groupReviewed && groupReviewStatus?.[c.id]?.reviews?.length > 0
        acc.groupReviewedByMe =
          acc.groupReviewedByMe && !!groupReviewStatus[c.id]?.review
      } else {
        if (!c.type || !c.id) {
          acc.groupReviewed = false
          acc.groupReviewedByMe = false
          return acc
        }

        const effectiveId =
          c.type === 'reconciliation_text'
            ? c.id
            : c.accountEntryId
            ? c.accountEntryId
            : c.id

        const key = `${c.type}:${effectiveId}`
        const data = reviewStatus[key]

        if (!data) {
          acc.groupReviewed = false
          acc.groupReviewedByMe = false
          return acc
        }

        acc.groupReviewed =
          acc.groupReviewed &&
          data?.reviews?.some((review) => review.outdatedAt === null)
        acc.groupReviewedByMe = acc.groupReviewedByMe && !!data?.review
      }

      return acc
    },
    {
      groupReviewed: true,
      groupReviewedByMe: true,
    }
  )

  // "Fake" review status data for the group, matching the structure of the review status data
  // used normally when displaying the review checkmark for the actual reconciliations
  // TODO: fix this down the line by streamlining the logic for the checkmark (show/hide and color)
  return {
    review: result.groupReviewedByMe
      ? { id: group.id, outdatedAt: null }
      : null,
    reviews: result.groupReviewed
      ? [{ name: 'group', time: Date.now(), outdatedAt: null }]
      : [],
  }
}

const prepareSearchAccountEntryItem = (
  item: WorkflowOverviewItem
): PreparedWorkflowOverviewAccountEntry => {
  return {
    id: item.id,
    preparedName: fuzzysort.prepare(item.name),
    preparedNumber: fuzzysort.prepare(item?.contextHrefParam || ''),
    preparedOriginalNumber: fuzzysort.prepare(item?.originalNumber || ''),
  }
}

const prepareSearchReconciliationTextItem = (
  item: WorkflowOverviewItem
): PreparedWorkflowOverviewReconciliationText => {
  return {
    id: item.id,
    preparedName: fuzzysort.prepare(item.name),
  }
}

export const prepareSearchWorkflowItem = (
  item: WorkflowOverviewItem | WorkflowOverviewGroup
):
  | PreparedWorkflowOverviewAccountEntry
  | PreparedWorkflowOverviewReconciliationText
  | null => {
  if (item.type === 'account_entry') {
    return prepareSearchAccountEntryItem(item)
  } else if (item.type === 'reconciliation_text') {
    return prepareSearchReconciliationTextItem(item)
  }

  return null
}

export const getPulseReconciliationStatusKey = (
  item: WorkflowOverviewItem | PulseReconciliationStatusData
): string => `${item.type || 'unknown'}:${item.id}`
